import { useEffect, useRef, useState } from 'react';

import { CheckableElement } from '../../../checkable-elements/CheckableElement';
import { GrammarCheckAlert } from '../../../types';

const useAlertRects = (
  checkableElement: CheckableElement,
  targetEl: HTMLElement,
  containerEl: HTMLElement | null,
  alerts: GrammarCheckAlert[],
) => {
  const [rects, setRects] = useState<DOMRect[][]>([]);
  const [hoveredRectIndexPrimitive, setHoveredRectIndexPrimitive] = useState<
    string | null
  >(null);
  const [hoveredRectIndex, setHoveredRectIndex] = useState<{
    alertIndex: number;
    rectIndex: number;
  } | null>(null);
  const rectsRef = useRef<DOMRect[][]>([]);
  const latestMouseCoords = useRef<{
    x: number;
    y: number;
  } | null>(null);

  const rectsToRelativePosition = (rects: DOMRect[][]) => {
    if (!containerEl) {
      return rects;
    }

    const containerElRect = containerEl.getBoundingClientRect();
    return rects.map((alertRects) => {
      return alertRects.map((rect) => {
        return new DOMRect(
          rect.left - containerElRect.left,
          rect.top - containerElRect.top + (containerEl.scrollTop || 0),
          rect.width,
          rect.height,
        );
      });
    });
  };

  const recalculateRects = (): void => {
    if (!alerts) {
      setRects([]);
      rectsRef.current = [];
    }

    setHoveredRectIndex(null);

    const calculatedRects = rectsToRelativePosition(
      checkableElement.calculateAlertClientRects(alerts),
    );
    setRects(calculatedRects);
    rectsRef.current = calculatedRects;

    recalculateHoveredRectIndex();
  };

  const recalculateHoveredRectIndex = () => {
    if (!rectsRef.current || !latestMouseCoords.current) {
      return;
    }

    const refRect = (containerEl || targetEl).getBoundingClientRect();
    const x = latestMouseCoords.current.x - refRect.left;
    const y =
      latestMouseCoords.current.y - refRect.top + (containerEl?.scrollTop || 0);

    for (let i = 0; i < rectsRef.current.length; i++) {
      for (let j = 0; j < rectsRef.current[i].length; j++) {
        const rect = rectsRef.current[i][j];
        if (
          x >= rect.left &&
          x <= rect.left + rect.width &&
          y >= rect.top &&
          y <= rect.top + rect.height + 3
        ) {
          const newHoveredIndexPrimitive = `${i}-${j}`;
          setHoveredRectIndexPrimitive(newHoveredIndexPrimitive);
          return;
        }
      }
    }

    setHoveredRectIndexPrimitive(null);
  };

  useEffect(() => {
    if (hoveredRectIndexPrimitive) {
      const [i, j] = hoveredRectIndexPrimitive.split('-').map(Number);
      setHoveredRectIndex({ alertIndex: i, rectIndex: j });
    } else {
      setHoveredRectIndex(null);
    }
  }, [hoveredRectIndexPrimitive]);

  useEffect(() => {
    recalculateRects();

    if (targetEl) {
      const handleScroll = (): void => {
        recalculateRects();
      };

      const handleMouseMove = (event: MouseEvent): void => {
        latestMouseCoords.current = { x: event.clientX, y: event.clientY };
        recalculateHoveredRectIndex();
      };

      targetEl.addEventListener('scroll', handleScroll);
      targetEl.addEventListener('mousemove', handleMouseMove);

      return (): void => {
        targetEl.removeEventListener('scroll', handleScroll);
        targetEl.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [targetEl, containerEl, alerts]);

  return { rects, hoveredRectIndex };
};

export default useAlertRects;
